import { ReactElement } from 'react'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import {
  CanceledIcon,
  DoneIcon,
  InProgressIcon,
  OverdueIcon,
  DueIcon,
  PendingIcon,
} from 'screens/Rostering/RotaBuilder/common/components/icons'
import {
  ClockInOutAction,
  ClockInOutType,
} from 'screens/Rostering/hooks/useClockInOut'
import { WarningsTolerance } from '@lmc/ts-common'
import { ClockIn, ClockInTeamMember } from 'screens/Rostering/types'

dayjs.extend(isoWeek)

type GetStatusArgs = {
  clockInOutDetails: ClockIn | undefined
}

type GetStatusReturn = {
  label: string
  icon: ReactElement
}

export function getStatus({
  clockInOutDetails,
}: GetStatusArgs): GetStatusReturn {
  const status = clockInOutDetails?.status

  const statusIconMap = {
    cancelled: { label: 'CANCELLED', icon: <CanceledIcon /> },
    pending: { label: 'PENDING', icon: <PendingIcon /> },
    due: { label: 'DUE', icon: <DueIcon /> },
    overdue: { label: 'OVERDUE', icon: <OverdueIcon /> },
    inProgress: { label: 'IN_PROGRESS', icon: <InProgressIcon /> },
    done: { label: 'COMPLETED', icon: <DoneIcon /> },
  }

  if (status) {
    type StatusIconMapKey = keyof typeof statusIconMap

    const result = statusIconMap[status as StatusIconMapKey]

    if (result) {
      return result
    }
  }

  if (clockInOutDetails?.clockedIn) {
    return statusIconMap.inProgress
  }

  return statusIconMap.pending
}

export function getWarnings(clockInWarnings: ClockInTeamMember['warnings']) {
  return [
    ...getClockInWarnings(clockInWarnings),
    ...getClockOutWarnings(clockInWarnings),
  ]
}

export function getClockInWarnings(
  clockInWarnings: ClockInTeamMember['warnings'],
) {
  const warnings: string[] = []

  if (clockInWarnings?.clockInEarly ?? 0) {
    warnings.push('CLOCK_IN_EARLY')
  }

  if (clockInWarnings?.clockInLate ?? 0) {
    warnings.push('CLOCK_IN_LATE')
  }

  return warnings
}

export function getClockOutWarnings(
  clockOutWarnings: ClockInTeamMember['warnings'],
) {
  const warnings: string[] = []

  if (clockOutWarnings?.clockOutLate ?? 0) {
    warnings.push('CLOCK_OUT_LATE')
  }

  if (clockOutWarnings?.clockOutEarly ?? 0) {
    warnings.push('CLOCK_OUT_EARLY')
  }

  return warnings
}

export function getDurationWarnings(
  durationWarnings: ClockInTeamMember['warnings'],
  warningsTolerance: WarningsTolerance | undefined,
) {
  const warnings = []

  if (
    (durationWarnings?.clockInMismatch ?? 0) >
    (warningsTolerance?.durationMismatched ?? 0)
  ) {
    warnings.push('DURATION_MISMATCH')
  }

  return warnings
}

export default getDurationWarnings

type GetPrefilledValuesArgs = {
  clockInOutDetails: ClockIn | undefined
  action: ClockInOutAction
  type: ClockInOutType
  timezone: string
}

type GetPrefilledValuesReturn = {
  date: dayjs.Dayjs
  time: dayjs.Dayjs
  note: string
}

export function getPrefilledValues({
  clockInOutDetails,
  action,
  type,
  timezone,
}: GetPrefilledValuesArgs): GetPrefilledValuesReturn {
  if (action === 'edit') {
    if (type === 'in') {
      const dateTime = dayjs(
        clockInOutDetails?.clockInTime || clockInOutDetails?.clockInTimeSystem,
      ).tz(timezone)
      return {
        date: dateTime,
        time: dateTime,
        note: clockInOutDetails?.clockInNotes || '',
      }
    }

    if (type === 'out') {
      const dateTime = dayjs(
        clockInOutDetails?.clockOutTime ||
          clockInOutDetails?.clockOutTimeSystem,
      ).tz(timezone)
      return {
        date: dateTime,
        time: dateTime,
        note: clockInOutDetails?.clockOutNotes || '',
      }
    }
  }

  return {
    date: dayjs(new Date()),
    time: dayjs(new Date()),
    note: '',
  }
}
